/**
 * useOfflineSync
 *
 * Tracks online/offline status and the sync queue state. Driven entirely by
 * the new push engine (sync-push.engine) — not the legacy flush() path.
 *
 * Previously this hook called flush() from offline-sync.service on every
 * `online` event, which raced with startPushLoop() in App.tsx (both read
 * the same sync_queue rows, sending them to two different endpoints).
 * That race is now gone — App.tsx owns the push loop exclusively.
 */

import { useState, useEffect, useCallback } from "react";

import { pushOnce } from "../services/sync-push.engine";
import { useSyncStatus } from "./useSyncStatus";

export type SyncState = "online" | "offline" | "syncing" | "conflict";

export function useOfflineSync() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const status = useSyncStatus();

  useEffect(() => {
    const onOnline  = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener("online",  onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online",  onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  // Drive syncState from the live engine counts, not from flush() result.
  const syncState: SyncState = !isOnline
    ? "offline"
    : status.syncing > 0
      ? "syncing"
      : status.conflict > 0
        ? "conflict"
        : "online";

  // pending shown in the visit page pill — all pending items across entity types.
  const pending = status.pending;

  // Allows a component to trigger a cycle immediately (e.g. a manual "sync now" button).
  // Does not return a result — use useSyncStatus() to observe the outcome.
  const manualFlush = useCallback(() => {
    pushOnce().catch(() => { /* outcome reflected in sync_queue, no throw needed */ });
  }, []);

  // Kept for backwards compat — the badge re-reads via subscribe() already,
  // but some call sites call refreshPending() after enqueue to update count.
  // With subscribe() active, this is a no-op; it's safe to leave in place.
  const refreshPending = useCallback(() => {
    // subscribe() in useSyncStatus handles live updates automatically.
    // This fn exists so NinetysDiagnosis's call sites still compile.
  }, []);

  return { isOnline, pending, syncState, manualFlush, refreshPending };
}
